import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangleIcon, BarChart3Icon, BookOpenIcon, CheckCircle2Icon, CheckIcon, CreditCardIcon, DropletsIcon, FactoryIcon, FileTextIcon, FuelIcon, GiftIcon, GraduationCapIcon, HardHatIcon, LandmarkIcon, LockIcon, MapIcon, ShieldCheckIcon, ShieldIcon, StarIcon, UsersIcon, WrenchIcon, XIcon, ZapIcon } from 'lucide-react';
import { Button } from './ui/Button';
import { Countdown } from './ui/Countdown';
import { FAQ } from './FAQ';
import { Footer } from './Footer';
import { PurchaseNotification } from './PurchaseNotification';
const productImage = 'https://cdn.magicpatterns.com/uploads/ucTR8E7HqZgAcDUKMCV71k/mockup.png';
const fadeUp = (delay = 0) => ({
  initial: {
    opacity: 0,
    y: 18
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    amount: 0.12
  },
  transition: {
    duration: 0.45,
    delay
  }
});
const testimonials = [['MG', 'María G.', 'Docente de Operación de Plantas · Neuquén', 'En una tarde tenía más material sobre petróleo y gas contextualizado en Argentina que en toda mi carrera docente. Ahora mis alumnos preguntan sobre Vaca Muerta, el GNK y el futuro del GNL, y tengo respuesta fundamentada para todo.', 'bg-forest'], ['RT', 'Rodrigo T.', 'Docente de Petróleo y Gas · Santa Cruz', 'Mis alumnos por primera vez entendieron la diferencia entre recursos convencionales y no convencionales, y analizaron el debate del fracking con argumentos técnicos reales. El nivel de participación se multiplicó.', 'bg-earth'], ['SP', 'Sandra P.', 'Profesora de Explotación de Hidrocarburos · Chubut', 'Los ejercicios de cálculo de declinación de producción y POES con datos de Vaca Muerta son exactamente lo que mis alumnos necesitaban: ingeniería técnica real con contexto argentino.', 'bg-gold-dark']];
const modules: Array<[string, string, string, React.ElementType, string, string, string]> = [['01', 'Fundamentos del Petróleo y Gas', 'Origen de los hidrocarburos, cuencas argentinas, grado API, recursos convencionales y no convencionales, YPF y soberanía energética.', FuelIcon, 'text-forest', 'bg-forest/10', 'ORIGEN HC · CUENCAS · API · YPF · VACA MUERTA · SHALE · GNL'], ['02', 'Exploración Geológica y Prospección', 'Sistemas petroleros, sísmica de reflexión, geoquímica orgánica e interpretación de reservas con datos de la Cuenca Neuquina y offshore argentino.', MapIcon, 'text-earth', 'bg-earth/10', 'SÍSMICA 3D · COT · RESERVAS P1/P2/P3 · OFFSHORE · SEGEMAR'], ['03', 'Perforación y Producción', 'Pozos horizontales, lodos, cementación, fractura multietapa, levantamiento artificial y cálculos de POES y declinación.', WrenchIcon, 'text-gold-dark', 'bg-gold/10', 'FRACKING · BOP · ESP · PAD DRILLING · SCADA · DECLINACIÓN'], ['04', 'Refinación, Transporte y Comercialización', 'Destilación, combustibles, petroquímica, oleoductos, gasoductos, GNK y la cadena de valor que conecta Vaca Muerta con el país.', FactoryIcon, 'text-forest', 'bg-forest/10', 'DESTILACIÓN · GNK · OPEP · OLDELVAL · BAHÍA BLANCA · GNL'], ['05', 'Seguridad, Ambiente y Legislación', 'H₂S, EPP, EIA, leyes energéticas, RIGI, prevención de derrames, remediación y debates ambientales con evidencia.', ShieldCheckIcon, 'text-earth', 'bg-earth/10', 'HAZOP · JSA · EIA · LEY 17.319 · RIGI · REMEDIACIÓN'], ['06', 'Didáctica del Petróleo y Gas', 'ABP, secuencias listas, experimentos de aula, recursos digitales y evaluación por competencias para enseñar con metodologías actuales.', GraduationCapIcon, 'text-gold-dark', 'bg-gold/10', 'ABP · EXPERIMENTOS · RECURSOS DIGITALES · SECUENCIAS 2026']];
const checklist = [['Contenido 100% argentino', 'Vaca Muerta, YPF, Oldelval, GNK, Cerro Dragón, Cuenca Neuquina y RIGI 2024.'], ['Material imprimible', 'Listo para usar en el aula o entregar al alumno.'], ['Fichas de referencia rápida', 'Para consultar mientras enseñás.'], ['Ejercicios resueltos', 'Con datos reales de yacimientos y empresas argentinas.'], ['Vocabulario técnico explicado', 'Rigor técnico sin tecnicismos innecesarios para el aula.'], ['Conexión con la actualidad', 'Vaca Muerta 2026, GNK, GNL, RIGI y transición energética.'], ['Contexto histórico argentino', 'Desde Comodoro 1907 hasta los proyectos de shale actuales.'], ['4 bonos estratégicos', 'Actividades, evaluaciones, atlas energético y planificaciones.'], ['Garantía de 7 días', 'Sin preguntas ni formularios.'], ['Acceso inmediato', 'Descarga directa tras la compra.']];
const bonuses = [['01', 'Banco de 100 Actividades para Docentes', 'Crucigramas, investigaciones, mapas conceptuales, problemas con datos reales, casos prácticos y trabajo colaborativo. Organizadas por nivel y tiempo disponible.', '$18.000'], ['02', 'Banco Completo de Evaluaciones', 'Diagnósticas, formativas, sumativas, 100 preguntas de opción múltiple, verdadero/falso, desarrollo y rúbricas con clave de corrección.', '$15.000'], ['03', 'Atlas Energético Argentino 2026', 'Mapas de cuencas productivas, fichas de yacimientos, red de oleoductos y gasoductos, refinerías, producción provincial y comparativas.', '$15.000'], ['04', 'Planificaciones Docentes Completas', 'Plan anual, trimestral y mensual 2026, objetivos por nivel, indicadores de logro, proyectos y cronograma de evaluaciones.', '$20.000']];
const premiumModules: Array<[string, string, React.ElementType]> = [['Petróleo y Gas Avanzado', 'EOR, waterflooding, CO₂, fractura hidráulica técnica, perforación horizontal de precisión, producción offshore, IA y automatización.', WrenchIcon], ['Vaca Muerta y la Energía Argentina', 'Historia 1927–2026, geología detallada, shale oil vs shale gas, infraestructura neuquina, controversias, impacto económico y escenarios 2050.', LandmarkIcon]];
const comparison = [['Preparar clases técnicas avanzadas', 'Buscás horas sin encontrar nivel suficiente', 'EOR, fractura avanzada, offshore e IA aplicada listos para usar'], ['Explicar Vaca Muerta en profundidad', 'Datos básicos sin análisis completo', 'Historia, geología, controversias y futuro 2050 integrados'], ['Hablar con autoridad del shale', 'Explicás lo básico', 'Shale oil vs shale gas: dos negocios distintos en una formación'], ['Responder el debate fracking-ambiente', 'Datos parciales sin profundidad', 'Argumentos técnicos con evidencia desde todos los ángulos'], ['Proyectar el futuro energético', 'Sin base para hablar de GNL y exportaciones', 'Escenarios 2030–2050, RIGI y geopolítica energética']];
function scrollToPricing() {
  document.getElementById('pricing')?.scrollIntoView({
    behavior: 'smooth',
    block: 'start'
  });
}
function CheckLine({
  children,
  gold = false
}: {
  children: React.ReactNode;
  gold?: boolean;
}) {
  return <li className="flex items-start gap-3 text-sm leading-snug text-forest-dark" data-id="element-22">
      <CheckIcon className={`mt-0.5 h-5 w-5 shrink-0 ${gold ? 'text-gold-dark' : 'text-forest'}`} data-id="element-23" />
      {children}
    </li>;
}
export function LandingPage() {
  return <div className="min-h-screen w-full overflow-hidden bg-base text-forest-dark" data-id="element-24">
      <main data-id="element-25">
        <section className="relative isolate overflow-hidden border-b border-sand bg-base pb-16 pt-12 sm:pb-20 sm:pt-16" data-id="element-26">
          <div className="absolute inset-x-0 top-0 -z-10 h-64 bg-sand/60" data-id="element-27" />
          <div className="absolute right-0 top-0 -z-10 h-80 w-80 border-b border-l border-earth/15" data-id="element-28" />
          <div className="mx-auto max-w-6xl px-4" data-id="element-29">
            <div className="mx-auto max-w-4xl text-center" data-id="element-30">
              <motion.p initial={{
              opacity: 0,
              y: -10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4
            }} className="inline-flex items-center gap-2 rounded-full border border-earth/20 bg-white px-4 py-2 text-xs font-extrabold uppercase tracking-[.14em] text-earth" data-id="element-31">
                <DropletsIcon className="h-4 w-4 fill-current" data-id="element-32" /> Megapack
                Petróleo y Gas Listo™
              </motion.p>
              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.08
            }} className="mx-auto mt-5 max-w-4xl text-balance text-4xl font-extrabold leading-[1.04] text-forest-dark sm:text-6xl md:text-7xl" data-id="element-33">
                Del pozo al aula.
                <br data-id="element-34" />
                <span className="text-forest" data-id="element-35">Todo resuelto.</span>
              </motion.h1>

              <motion.div initial={{
              opacity: 0,
              scale: 0.94
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.6,
              delay: 0.22
            }} className="mx-auto my-5 max-w-xl sm:my-7" data-id="element-36">
                <img src={productImage} alt="Megapack Petróleo y Gas Listo con los seis tomos, cuatro bonos y módulos premium" className="mx-auto w-full drop-shadow-[0_24px_28px_rgba(31,61,43,.2)]" data-id="element-37" />
              </motion.div>
              <div className="flex flex-wrap justify-center gap-2 text-xs font-bold text-forest-dark" data-id="element-38">
                {['Acceso inmediato', 'Hecho para Argentina', 'Pago seguro'].map(item => <span key={item} className="rounded-full border border-sand bg-white px-3 py-2" data-id="element-39">
                    {item}
                  </span>)}
              </div>
              <div className="mt-7" data-id="element-40">
                <Button onClick={scrollToPricing} arrow data-id="element-41">
                  QUIERO MI MEGAPACK AHORA
                </Button>
              </div>
              <div className="mt-6 flex flex-wrap justify-center gap-x-5 gap-y-2 text-xs font-semibold text-muted" data-id="element-42">
                <span className="inline-flex items-center gap-1.5" data-id="element-43">
                  <ShieldIcon className="h-4 w-4 text-forest" data-id="element-44" /> Garantía 7 días
                </span>
                <span className="inline-flex items-center gap-1.5" data-id="element-45">
                  <LockIcon className="h-4 w-4 text-forest" data-id="element-46" /> Pago seguro
                </span>
                <span className="inline-flex items-center gap-1.5" data-id="element-47">
                  <CreditCardIcon className="h-4 w-4 text-earth" data-id="element-48" /> Mercado Pago
                </span>
              </div>
            </div>
          </div>
        </section>

        <section className="border-b border-sand bg-white py-5" aria-label="Información del producto" data-id="element-49">
          <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-center gap-x-8 gap-y-3 px-4 text-sm font-semibold text-muted sm:justify-between" data-id="element-50">
            <span className="inline-flex items-center gap-2" data-id="element-51">
              <UsersIcon className="h-5 w-5 text-earth" data-id="element-52" /> +200 docentes
            </span>
            <span className="inline-flex items-center gap-2" data-id="element-53">
              <BookOpenIcon className="h-5 w-5 text-earth" data-id="element-54" /> 6 tomos completos
            </span>
            <span className="inline-flex items-center gap-2" data-id="element-55">
              <GiftIcon className="h-5 w-5 text-earth" data-id="element-56" /> 4 bonos incluidos
            </span>
            <span className="inline-flex items-center gap-2" data-id="element-57">
              <StarIcon className="h-5 w-5 fill-gold text-gold" data-id="element-58" /> 4.9/5
              calificación
            </span>
          </div>
        </section>

        <section className="bg-base py-16 sm:py-20" aria-labelledby="reviews-title" data-id="element-59">
          <div className="mx-auto max-w-6xl px-4" data-id="element-60">
            <p className="text-center text-xs font-extrabold uppercase tracking-[.18em] text-earth" data-id="element-61">
              Experiencias reales
            </p>
            <h2 id="reviews-title" className="mt-3 text-center text-3xl font-extrabold text-forest-dark sm:text-4xl" data-id="element-62">
              Lo que dicen quienes ya lo tienen
            </h2>
            <div className="mt-10 grid gap-5 md:grid-cols-3" data-id="element-63">
              {testimonials.map(([initials, name, role, quote, color], index) => <motion.article key={name} {...fadeUp(index * 0.08)} className="flex min-h-[255px] flex-col rounded-2xl border border-sand bg-white p-6 shadow-card" data-id="element-64">
                    <div className="flex text-gold" data-id="element-65">
                      {Array.from({
                  length: 5
                }).map((_, star) => <StarIcon key={star} className="h-4 w-4 fill-current" data-id="element-66" />)}
                    </div>
                    <blockquote className="mt-4 flex-1 text-sm italic leading-relaxed text-muted" data-id="element-67">
                      “{quote}”
                    </blockquote>
                    <div className="mt-5 flex items-center gap-3 border-t border-sand pt-4" data-id="element-68">
                      <span className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-extrabold text-white ${color}`} data-id="element-69">
                        {initials}
                      </span>
                      <span data-id="element-70">
                        <b className="block text-sm text-forest-dark" data-id="element-71">{name}</b>
                        <small className="text-xs text-muted" data-id="element-72">{role}</small>
                      </span>
                    </div>
                  </motion.article>)}
            </div>
          </div>
        </section>

        <section className="bg-white py-16 sm:py-20" aria-labelledby="problem-title" data-id="element-73">
          <div className="mx-auto max-w-4xl px-4" data-id="element-74">
            <div className="text-center" data-id="element-75">
              <p className="text-xs font-extrabold uppercase tracking-[.18em] text-earth" data-id="element-76">
                La realidad docente
              </p>
              <h2 id="problem-title" className="mx-auto mt-3 max-w-3xl text-balance text-3xl font-extrabold leading-tight sm:text-4xl" data-id="element-77">
                ¿Cuántas clases más vas a preparar sobre petróleo y gas sin
                tener el material correcto?
              </h2>
              <p className="mx-auto mt-5 max-w-3xl leading-relaxed text-muted" data-id="element-78">
                Buscás información sin secuencia didáctica ni ejemplos
                argentinos. Y cuando surge Vaca Muerta, el Gasoducto Néstor
                Kirchner, el GNL o el shale, te falta un recurso que conecte a
                tus alumnos con la realidad energética del país.
              </p>
              <p className="mt-4 font-bold text-forest" data-id="element-79">
                No es falta de vocación docente. Es que nadie había armado el
                sistema completo para vos.
              </p>
            </div>
            <motion.div {...fadeUp(0.1)} className="mt-9 rounded-2xl border border-red-200 bg-red-50 p-6 sm:p-8" data-id="element-80">
              <p className="mb-5 font-serif text-xl font-bold text-red-800" data-id="element-81">
                Esto quizás te está pasando hoy:
              </p>
              <ul className="grid gap-4 sm:grid-cols-2" data-id="element-82">
                {['Perdés tiempo buscando material que casi nunca tiene ejemplos argentinos.', 'Tus alumnos se desconectan de ejemplos que no hablan de su país.', 'No podés conectar petróleo y gas con Vaca Muerta, GNK y GNL.', 'Tus evaluaciones se repiten por falta de ejercicios técnicos actualizados.', 'Llegás al ciclo lectivo sin una secuencia completa lista para usar.', 'El debate petróleo-ambiente te encuentra sin datos técnicos para equilibrarlo.'].map(item => <li key={item} className="flex gap-3 text-sm leading-relaxed text-forest-dark" data-id="element-83">
                    <span className="mt-0.5 rounded-full bg-red-100 p-1" data-id="element-84">
                      <XIcon className="h-3.5 w-3.5 text-red-700" data-id="element-85" />
                    </span>
                    {item}
                  </li>)}
              </ul>
            </motion.div>
            <p className="mt-8 text-center font-bold text-earth" data-id="element-86">
              ¿Y si esta fuera la última semana que enseñás petróleo y gas sin
              tener todo resuelto?
            </p>
          </div>
        </section>

        <section className="bg-base py-16 sm:py-20" aria-labelledby="tomos-title" data-id="element-87">
          <div className="mx-auto max-w-6xl px-4" data-id="element-88">
            <div className="text-center" data-id="element-89">
              <span className="inline-flex items-center gap-2 rounded-full border border-forest/20 bg-forest/10 px-4 py-2 text-xs font-extrabold uppercase tracking-[.15em] text-forest" data-id="element-90">
                <DropletsIcon className="h-4 w-4 fill-current" data-id="element-91" /> Los 6 tomos
              </span>
              <h2 id="tomos-title" className="mx-auto mt-4 max-w-3xl text-balance text-3xl font-extrabold sm:text-4xl" data-id="element-92">
                Un tomo por cada dimensión del petróleo y gas argentino
              </h2>
            </div>
            <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-id="element-93">
              {modules.map(([number, title, description, Icon, color, bg, tags], index) => <motion.article key={title} {...fadeUp(index * 0.06)} className="flex flex-col rounded-2xl border border-sand bg-white p-6 shadow-card" data-id="element-94">
                    <div className="flex items-start justify-between gap-4" data-id="element-95">
                      <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${bg} ${color}`} data-id="element-96">
                        <Icon className="h-6 w-6" data-id="element-97" />
                      </span>
                      <span className="font-serif text-2xl font-bold text-sand" data-id="element-98">
                        Tomo {number}
                      </span>
                    </div>
                    <h3 className="mt-5 text-xl font-bold text-forest-dark" data-id="element-99">
                      {title}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-muted" data-id="element-100">
                      {description}
                    </p>
                    <p className="mt-5 border-t border-sand pt-4 text-[10px] font-extrabold uppercase tracking-[.1em] text-earth" data-id="element-101">
                      {tags}
                    </p>
                  </motion.article>)}
            </div>
          </div>
        </section>

        <section className="border-y border-sand bg-white py-16" aria-labelledby="includes-title" data-id="element-102">
          <div className="mx-auto max-w-5xl px-4" data-id="element-103">
            <h2 id="includes-title" className="text-center text-3xl font-extrabold text-forest-dark" data-id="element-104">
              Todos los tomos incluyen
            </h2>
            <div className="mt-9 grid gap-x-10 gap-y-4 md:grid-cols-2" data-id="element-105">
              {checklist.map(([title, detail], index) => <motion.div key={title} {...fadeUp(index * 0.03)} className="flex gap-3 rounded-xl p-2" data-id="element-106">
                  <CheckCircle2Icon className="mt-0.5 h-5 w-5 shrink-0 text-forest" data-id="element-107" />
                  <p data-id="element-108">
                    <b className="block text-sm text-forest-dark" data-id="element-109">{title}</b>
                    <span className="text-sm text-muted" data-id="element-110">{detail}</span>
                  </p>
                </motion.div>)}
            </div>
          </div>
        </section>

        <section className="relative overflow-hidden bg-forest py-16 text-white sm:py-20" aria-labelledby="bonos-title" data-id="element-111">
          <div className="absolute right-0 top-0 h-40 w-40 border-b border-l border-gold/30" data-id="element-112" />
          <div className="absolute bottom-0 left-0 h-36 w-36 border-r border-t border-earth/50" data-id="element-113" />
          <div className="relative mx-auto max-w-5xl px-4" data-id="element-114">
            <div className="text-center" data-id="element-115">
              <GiftIcon className="mx-auto h-10 w-10 text-gold" data-id="element-116" />
              <p className="mt-4 text-xs font-extrabold uppercase tracking-[.18em] text-gold" data-id="element-117">
                Siempre incluidos · siempre gratis
              </p>
              <h2 id="bonos-title" className="mt-3 text-3xl font-extrabold sm:text-4xl" data-id="element-118">
                4 bonos estratégicos para docentes
              </h2>
            </div>
            <div className="mt-10 grid gap-5 md:grid-cols-2" data-id="element-119">
              {bonuses.map(([number, title, description, value], index) => <motion.article key={title} {...fadeUp(index * 0.06)} className="flex flex-col rounded-2xl border border-forest-soft bg-forest-dark p-6" data-id="element-120">
                  <span className="mb-5 w-fit rounded-full bg-forest-soft px-3 py-1 text-[10px] font-extrabold uppercase tracking-wider text-gold" data-id="element-121">
                    Bono {number}
                  </span>
                  <h3 className="text-xl font-bold" data-id="element-122">{title}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-sand" data-id="element-123">
                    {description}
                  </p>
                  <p className="mt-5 border-t border-forest pt-4 text-sm" data-id="element-124">
                    <span className="text-sand/50 line-through" data-id="element-125">
                      Valor {value}
                    </span>
                    <span className="float-right font-extrabold text-gold" data-id="element-126">
                      GRATIS
                    </span>
                  </p>
                </motion.article>)}
            </div>
            <p className="mx-auto mt-8 max-w-xl rounded-xl border border-white/20 bg-white/10 px-5 py-4 text-center text-sm" data-id="element-127">
              Valor total de bonos{' '}
              <span className="mx-1 line-through text-white/50" data-id="element-128">
                $68.000 ARS
              </span>{' '}
              <b className="text-gold" data-id="element-129">→ GRATIS con tu Megapack</b>
            </p>
          </div>
        </section>

        <section className="border-y-4 border-red-800 bg-red-700 py-12 text-center text-white" aria-label="Oferta por tiempo limitado" data-id="element-130">
          <div className="mx-auto max-w-3xl px-4" data-id="element-131">
            <p className="text-xs font-extrabold uppercase tracking-[.16em] text-red-100" data-id="element-132">
              Precio de lanzamiento · cierra en
            </p>
            <h2 className="mt-2 font-serif text-2xl font-extrabold sm:text-3xl" data-id="element-133">
              Tu aula puede estar lista hoy
            </h2>
            <Countdown light className="mt-6 scale-110 shadow-lg" data-id="element-134" />
            <p className="mt-7 font-bold text-red-100" data-id="element-135">
              Después sube. No vuelve al precio de lanzamiento.
            </p>
            <div className="mx-auto mt-6 max-w-xl rounded-xl border border-red-500/60 bg-red-800/40 p-4 text-sm" data-id="element-136">
              <p className="font-bold" data-id="element-137">
                <span className="mr-2 inline-block h-2.5 w-2.5 animate-pulse rounded-full bg-gold" data-id="element-138" />
                Cupos al precio de lanzamiento: quedan pocos
              </p>
              <div className="mt-3 h-3 overflow-hidden rounded-full bg-red-950/50" data-id="element-139">
                <motion.div initial={{
                width: 0
              }} whileInView={{
                width: '71%'
              }} viewport={{
                once: true
              }} transition={{
                duration: 1
              }} className="h-full rounded-full bg-gold" data-id="element-140" />
              </div>
              <p className="mt-1 text-right text-xs text-red-100" data-id="element-141">
                71% tomados
              </p>
            </div>
          </div>
        </section>

        <section id="pricing" className="scroll-mt-4 bg-base py-16 sm:py-24" aria-labelledby="pricing-title" data-id="element-142">
          <div className="mx-auto max-w-5xl px-4" data-id="element-143">
            <div className="text-center" data-id="element-144">
              <p className="text-xs font-extrabold uppercase tracking-[.18em] text-earth" data-id="element-145">
                Acceso inmediato
              </p>
              <h2 id="pricing-title" className="mt-3 text-3xl font-extrabold sm:text-4xl" data-id="element-146">
                Elegí tu Megapack
              </h2>
              <p className="mt-3 text-muted" data-id="element-147">
                Dos opciones · Mismo acceso inmediato
              </p>
            </div>
            <div className="mt-12 grid items-stretch gap-7 lg:grid-cols-2" data-id="element-148">
              <article className="flex flex-col rounded-3xl border border-sand bg-white p-6 shadow-card sm:p-8" data-id="element-149">
                <p className="text-xs font-extrabold uppercase tracking-[.15em] text-muted" data-id="element-150">
                  Opción 1
                </p>
                <h3 className="mt-3 text-2xl font-bold" data-id="element-151">
                  Megapack Petróleo y Gas Listo™
                </h3>
                <p className="mt-2 font-bold text-forest" data-id="element-152">6 tomos + 4 bonos</p>
                <div className="mt-7" data-id="element-153">
                  <span className="text-sm text-muted line-through" data-id="element-154">
                    $72.000 ARS
                  </span>
                  <p className="mt-1 font-serif text-5xl font-extrabold text-forest-dark" data-id="element-155">
                    $19.990 <small className="font-sans text-base" data-id="element-156">ARS</small>
                  </p>
                </div>
                <ul className="mt-7 flex-1 space-y-3" data-id="element-157">
                  {['Tomo 1: Fundamentos del Petróleo y Gas', 'Tomo 2: Exploración Geológica y Prospección', 'Tomo 3: Perforación y Producción', 'Tomo 4: Refinación, Transporte y Comercialización', 'Tomo 5: Seguridad, Ambiente y Legislación', 'Tomo 6: Didáctica del Petróleo y Gas', '4 bonos estratégicos para docentes', 'Acceso de por vida y actualizaciones gratuitas'].map(item => <CheckLine key={item} data-id="element-158">{item}</CheckLine>)}
                </ul>
                <a href="https://www.centralpack.shop/cart/53901341851966:1" target="_blank" rel="noopener noreferrer" className="mt-8 block" data-id="element-159">
                  <Button variant="forest" arrow className="w-full" data-id="element-160">
                    QUIERO MI MEGAPACK
                  </Button>
                </a>
              </article>

              <article className="relative flex flex-col rounded-3xl border-2 border-gold bg-white p-6 shadow-lift sm:p-8" data-id="element-161">
                <span className="absolute -top-4 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-gold px-4 py-2 text-xs font-extrabold text-white shadow-lg" data-id="element-162">
                  <StarIcon className="mr-1 inline h-3.5 w-3.5 fill-current" data-id="element-163" />{' '}
                  MÁS ELEGIDO
                </span>
                <p className="mt-3 text-xs font-extrabold uppercase tracking-[.15em] text-muted" data-id="element-164">
                  Opción 2
                </p>
                <h3 className="mt-3 text-2xl font-bold" data-id="element-165">
                  Megapack + Upsell Premium
                </h3>
                <p className="mt-2 font-bold text-gold-dark" data-id="element-166">
                  6 tomos + 4 bonos + 2 módulos avanzados
                </p>
                <div className="mt-7" data-id="element-167">
                  <span className="text-sm text-muted line-through" data-id="element-168">
                    $110.000 ARS
                  </span>
                  <p className="mt-1 font-serif text-5xl font-extrabold text-forest-dark" data-id="element-169">
                    $27.990 <small className="font-sans text-base" data-id="element-170">ARS</small>
                  </p>
                </div>
                <ul className="mt-7 flex-1 space-y-3" data-id="element-171">
                  <CheckLine gold data-id="element-172">
                    <b data-id="element-173">Todo el Megapack base, más:</b>
                  </CheckLine>
                  {['Petróleo y Gas Avanzado: EOR, offshore, fractura avanzada e IA', 'Vaca Muerta y la Energía Argentina: historia, geología y escenarios 2050', 'Shale oil vs shale gas con profundidad operativa', 'GNL, exportaciones y geopolítica energética', 'Acceso de por vida y actualizaciones gratuitas'].map(item => <CheckLine key={item} gold data-id="element-174">
                      <b className="text-gold-dark" data-id="element-175">{item}</b>
                    </CheckLine>)}
                </ul>
                <a href="https://www.centralpack.shop/cart/53901356728638:1" target="_blank" rel="noopener noreferrer" className="mt-8 block" data-id="element-176">
                  <Button arrow className="w-full" data-id="element-177">
                    QUIERO EL MEGAPACK PREMIUM
                  </Button>
                </a>
                <p className="mt-4 text-center text-xs text-muted" data-id="element-178">
                  Tarjeta, transferencia o Mercado Pago en pesos argentinos.
                </p>
              </article>
            </div>
          </div>
        </section>

        <section className="bg-white py-16 sm:py-20" aria-labelledby="premium-title" data-id="element-179">
          <div className="mx-auto max-w-5xl px-4" data-id="element-180">
            <div className="text-center" data-id="element-181">
              <p className="text-xs font-extrabold uppercase tracking-[.18em] text-earth" data-id="element-182">
                La diferencia premium
              </p>
              <h2 id="premium-title" className="mt-3 text-3xl font-extrabold sm:text-4xl" data-id="element-183">
                ¿Qué agrega el Upsell Premium?
              </h2>
            </div>
            <div className="mt-10 overflow-x-auto pb-3" data-id="element-184">
              <div className="min-w-[700px]" data-id="element-185">
                <div className="grid grid-cols-3 gap-4 px-4 text-xs font-extrabold uppercase tracking-wider" data-id="element-186">
                  <p className="text-muted" data-id="element-187">Situación</p>
                  <p className="text-red-600" data-id="element-188">Sin Premium</p>
                  <p className="text-forest" data-id="element-189">Con Premium</p>
                </div>
                <div className="mt-3 space-y-2" data-id="element-190">
                  {comparison.map(([situation, without, withPremium]) => <div key={situation} className="grid grid-cols-3 gap-4 rounded-xl border border-sand bg-base p-4 text-sm" data-id="element-191">
                      <p className="font-bold text-forest-dark" data-id="element-192">{situation}</p>
                      <p className="text-muted" data-id="element-193">{without}</p>
                      <p className="rounded-lg border border-forest/20 bg-forest/10 p-2 font-semibold text-forest" data-id="element-194">
                        {withPremium}
                      </p>
                    </div>)}
                </div>
              </div>
            </div>
            <p className="mt-1 text-center text-xs text-muted md:hidden" data-id="element-195">
              Deslizá para ver la tabla completa
            </p>
            <div className="mt-12 rounded-3xl bg-base p-6 shadow-card sm:p-10" data-id="element-196">
              <h3 className="text-2xl font-bold text-forest-dark" data-id="element-197">
                Dos módulos avanzados para enseñar con profundidad
              </h3>
              <div className="mt-7 grid gap-5 md:grid-cols-2" data-id="element-198">
                {premiumModules.map(([title, description, Icon], index) => <motion.article key={title} {...fadeUp(index * 0.06)} className="rounded-2xl border border-sand bg-white p-6" data-id="element-199">
                    <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-gold/10 text-gold-dark" data-id="element-200">
                      <Icon className="h-5 w-5" data-id="element-201" />
                    </span>
                    <h4 className="mt-4 text-lg font-bold" data-id="element-202">{title}</h4>
                    <p className="mt-2 text-sm leading-relaxed text-muted" data-id="element-203">
                      {description}
                    </p>
                  </motion.article>)}
              </div>
            </div>
          </div>
        </section>

        <section className="border-y border-sand bg-base py-16 sm:py-20" aria-labelledby="guarantee-title" data-id="element-204">
          <div className="mx-auto max-w-2xl px-4 text-center" data-id="element-205">
            <motion.div {...fadeUp()} className="rounded-3xl border border-forest/20 bg-white p-8 shadow-card sm:p-12" data-id="element-206">
              <span className="inline-flex rounded-full border border-forest/20 bg-forest/10 p-4 text-forest" data-id="element-207">
                <ShieldCheckIcon className="h-10 w-10" data-id="element-208" />
              </span>
              <h2 id="guarantee-title" className="mt-6 text-3xl font-extrabold" data-id="element-209">
                Garantía de 7 días, sin preguntas
              </h2>
              <p className="mt-4 leading-relaxed text-muted" data-id="element-210">
                Abrís los tomos. Los usás. Si en 7 días no es lo que esperabas,
                solicitás tu devolución.
              </p>
              <p className="mt-6 rounded-xl border border-forest/20 bg-forest/10 px-5 py-4 text-lg font-extrabold text-forest" data-id="element-211">
                Devolución del 100% sin formularios, sin drama.
              </p>
              <p className="mt-5 text-xs font-bold uppercase tracking-[.14em] text-muted" data-id="element-212">
                Tasa de devoluciones: menor al 1%
              </p>
            </motion.div>
          </div>
        </section>

        <FAQ data-id="element-213" />

        <section className="bg-base py-16 sm:py-20" aria-labelledby="cost-title" data-id="element-214">
          <div className="mx-auto max-w-4xl px-4" data-id="element-215">
            <h2 id="cost-title" className="text-center text-3xl font-extrabold sm:text-4xl" data-id="element-216">
              El costo de no tenerlo
            </h2>
            <div className="mt-9 overflow-x-auto pb-3" data-id="element-217">
              <div className="min-w-[600px]" data-id="element-218">
                <div className="grid grid-cols-2 gap-4 px-4 text-xs font-extrabold uppercase tracking-wider" data-id="element-219">
                  <p className="text-muted" data-id="element-220">Sin el Megapack</p>
                  <p className="text-red-600" data-id="element-221">Lo que realmente perdés</p>
                </div>
                <div className="mt-3 space-y-2" data-id="element-222">
                  {[['Sin ejemplos argentinos', 'Alumnos desconectados de por qué el petróleo y gas argentino importan.'], ['Sin banco de actividades', 'Horas semanales armando material desde cero.'], ['Sin casos de estudio equilibrados', 'Debates petróleo-ambiente sin fundamento técnico real.'], ['Sin atlas energético', 'Sin mapas de cuencas, oleoductos ni gasoductos para el aula.'], ['Sin conexión con la actualidad', 'Alumnos que no saben que el GNK cambió la historia energética argentina.'], ['Sin planificaciones listas', 'Horas de diseño que podrían dedicarse a enseñar.']].map(([without, cost]) => <div key={without} className="grid grid-cols-2 gap-4 rounded-xl border border-sand bg-white p-4 text-sm" data-id="element-223">
                      <p className="font-medium text-muted" data-id="element-224">{without}</p>
                      <p className="font-semibold text-red-700" data-id="element-225">{cost}</p>
                    </div>)}
                </div>
              </div>
            </div>
            <motion.blockquote {...fadeUp(0.1)} className="mt-10 rounded-2xl border border-sand bg-white p-7 text-center shadow-card sm:p-10" data-id="element-226">
              <p className="font-serif text-xl font-bold leading-relaxed text-forest sm:text-2xl" data-id="element-227">
                “La próxima vez que un alumno pregunte para qué sirve estudiar
                petróleo y gas, podés mostrarle Vaca Muerta, el gasoducto que
                transformó la matriz energética y el potencial del GNL
                argentino. O podés seguir explicando sin ese contexto.”
              </p>
              <DropletsIcon className="mx-auto mt-5 h-8 w-8 fill-current text-gold-dark" data-id="element-228" />
            </motion.blockquote>
          </div>
        </section>

        <section className="border-t border-sand bg-white py-16 sm:py-20" aria-labelledby="final-title" data-id="element-229">
          <div className="mx-auto max-w-3xl px-4 text-center" data-id="element-230">
            <p className="text-xs font-extrabold uppercase tracking-[.16em] text-red-600" data-id="element-231">
              El precio de lanzamiento cierra en
            </p>
            <Countdown className="mt-4" data-id="element-232" />
            <h2 id="final-title" className="mx-auto mt-8 max-w-2xl text-balance text-3xl font-extrabold sm:text-4xl" data-id="element-233">
              Elegí el recurso que te devuelve tiempo y eleva tus clases
              técnicas
            </h2>
            <div className="mt-9 space-y-4 text-left" data-id="element-234">
              <div className="flex flex-col gap-4 rounded-2xl border border-sand bg-base p-5 sm:flex-row sm:items-center sm:justify-between" data-id="element-235">
                <p data-id="element-236">
                  <b className="block text-forest-dark" data-id="element-237">
                    MEGAPACK PETRÓLEO Y GAS LISTO™
                  </b>
                  <span className="text-sm text-muted" data-id="element-238">6 tomos + 4 bonos</span>
                </p>
                <div className="sm:text-right" data-id="element-239">
                  <p data-id="element-240">
                    <span className="mr-2 text-sm text-muted line-through" data-id="element-241">
                      $72.000
                    </span>
                    <b data-id="element-242">$19.990 ARS</b>
                  </p>
                  <a href="https://www.centralpack.shop/cart/53901341851966:1" target="_blank" rel="noopener noreferrer" className="mt-2 inline-block" data-id="element-243">
                    <Button size="sm" variant="outline" data-id="element-244">
                      ELEGIR MEGAPACK
                    </Button>
                  </a>
                </div>
              </div>
              <div className="relative flex flex-col gap-4 rounded-2xl border-2 border-gold bg-white p-5 shadow-card sm:flex-row sm:items-center sm:justify-between" data-id="element-245">
                <span className="absolute right-0 top-0 rounded-bl-lg bg-gold px-2 py-1 text-[10px] font-extrabold text-white" data-id="element-246">
                  MÁS ELEGIDO
                </span>
                <p data-id="element-247">
                  <b className="block text-forest-dark" data-id="element-248">
                    MEGAPACK + UPSELL PREMIUM
                  </b>
                  <span className="text-sm text-muted" data-id="element-249">
                    6 tomos + 4 bonos + 2 módulos avanzados
                  </span>
                </p>
                <div className="sm:text-right" data-id="element-250">
                  <p data-id="element-251">
                    <span className="mr-2 text-sm text-muted line-through" data-id="element-252">
                      $110.000
                    </span>
                    <b data-id="element-253">$27.990 ARS</b>
                  </p>
                  <a href="https://www.centralpack.shop/cart/53901356728638:1" target="_blank" rel="noopener noreferrer" className="mt-2 inline-block" data-id="element-254">
                    <Button size="sm" data-id="element-255">ELEGIR PREMIUM</Button>
                  </a>
                </div>
              </div>
            </div>
            <div className="mt-8 flex flex-wrap justify-center gap-x-5 gap-y-2 text-xs font-semibold text-muted" data-id="element-256">
              <span className="inline-flex gap-1" data-id="element-257">
                <LockIcon className="h-4 w-4 text-forest" data-id="element-258" /> Pago 100% seguro
              </span>
              <span className="inline-flex gap-1" data-id="element-259">
                <ZapIcon className="h-4 w-4 text-gold-dark" data-id="element-260" /> Acceso en 5
                minutos
              </span>
              <span className="inline-flex gap-1" data-id="element-261">
                <ShieldIcon className="h-4 w-4 text-forest" data-id="element-262" /> Garantía 7 días
              </span>
              <span className="inline-flex gap-1" data-id="element-263">
                <CreditCardIcon className="h-4 w-4 text-earth" data-id="element-264" /> Mercado Pago ·
                Tarjeta · Transferencia
              </span>
            </div>
          </div>
        </section>
      </main>
      <Footer data-id="element-265" />
      <PurchaseNotification data-id="element-266" />
    </div>;
}